import React from "react"
import { useUpload } from "@/contexts/UploadContext"
import UploadItem from "./UploadItem"
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { X } from "lucide-react"
import { uploadService } from "@/services/uploadService"

/**
 * Props for the {@link UploadQueueModal} component.
 */
interface UploadQueueModalProps {
	/** Whether the modal is currently open. */
	isOpen: boolean
	/** Callback function to close the modal. */
	onClose: () => void
}

/**
 * A modal dialog that displays a detailed list of all current, pending, and
 * recently completed/failed uploads.
 * It allows users to view progress, status, and manage their uploads (e.g., cancel, retry, resume).
 *
 * @component
 * @param props - The props for the component.
 * @returns A React element representing the upload queue modal.
 */
const UploadQueueModal: React.FC<UploadQueueModalProps> = ({
	isOpen,
	onClose,
}) => {
	const { uploadState, dispatchUploadAction } = useUpload()

	const handleCancelAll = async () => {
		const tasksToCancel = uploadState.tasks.filter(
			(task) =>
				task.status === "uploading" ||
				task.status === "pending" ||
				task.status === "paused" ||
				task.status === "pending_resume"
		)

		await Promise.all(
			tasksToCancel.map(async (task) => {
				await uploadService.abortUpload(task, dispatchUploadAction)
				dispatchUploadAction({ type: "REMOVE_TASK", payload: { id: task.id } })
			})
		)
	}

	const handleClearCompleted = () => {
		uploadState.tasks.forEach((task) => {
			if (task.status === "completed" || task.status === "error") {
				dispatchUploadAction({ type: "REMOVE_TASK", payload: { id: task.id } })
			}
		})
	}

	const handleClearErrors = () => {
		uploadState.tasks.forEach((task) => {
			if (task.status === "error") {
				dispatchUploadAction({ type: "REMOVE_TASK", payload: { id: task.id } })
			}
		})
	}

	if (!isOpen) {
		return null
	}

	return (
		<Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
			<DialogContent className="sm:max-w-2xl max-h-[80vh] flex flex-col">
				<DialogHeader>
					<DialogTitle>Upload Queue</DialogTitle>
					<DialogDescription>
						Manage your file uploads. Files pending resume require you to
						re-select the file.
					</DialogDescription>
				</DialogHeader>

				{uploadState.tasks.length === 0 ? (
					<div className="flex-grow flex items-center justify-center">
						<p className="text-muted-foreground">
							No active or recent uploads.
						</p>
					</div>
				) : (
					<div className="flex-grow overflow-y-auto space-y-3 pr-1">
						{uploadState.tasks.map((task) => (
							<UploadItem key={task.id} task={task} />
						))}
					</div>
				)}

				<DialogFooter className="mt-4 flex-shrink-0">
					{uploadState.tasks.some((task) => task.status === "error") && (
						<Button variant="outline" onClick={handleClearErrors} size="sm">
							Clear All Errors
						</Button>
					)}
					{uploadState.tasks.some(
						(task) => task.status === "completed" || task.status === "error"
					) && (
						<Button variant="outline" onClick={handleClearCompleted} size="sm">
							Clear Finished
						</Button>
					)}
					{uploadState.tasks.some(
						(task) =>
							task.status === "uploading" ||
							task.status === "pending" ||
							task.status === "paused" ||
							task.status === "pending_resume"
					) && (
						<Button variant="destructive" onClick={handleCancelAll} size="sm">
							Cancel All Active
						</Button>
					)}
					<Button variant="secondary" onClick={onClose} size="sm">
						Close
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}

export default UploadQueueModal
